import { openDB } from "idb";
import _ from "lodash";

const DB_NAME = "VehicleInspectionDB";
const DB_VERSION = 1;
const STORE_NAME = "carData";

// Initialize the database
export const initializeDB = async () => {
  try {
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "sessionId" });
        }
      },
    });
    return db;
  } catch (error) {
    console.error("Failed to initialize IndexedDB:", error);
    throw error;
  }
};

// Store car data to IndexedDB
const sanitizeCarData = (carData) => {
  return carData.map((item) => {
    // Remove or stringify any function, undefined, or unexpected types
    const sanitized = { ...item };

    Object.keys(sanitized).forEach((key) => {
      const val = sanitized[key];
      // Replace undefined with null
      if (val === undefined) sanitized[key] = null;

      // Remove functions or other non-serializable items if any
      if (typeof val === "function") delete sanitized[key];

      // Handle other special cases if required
    });

    return sanitized;
  });
};

export const storeCarData = async (sessionId, carData) => {
  try {
    const db = await initializeDB();

    // Deep clone to avoid revoked proxy issues
    // const safeCarData = JSON.parse(JSON.stringify(carData));
    const sanitizedCarData = sanitizeCarData(carData);

    const dataToStore = {
      sessionId,
      carData: sanitizedCarData,
      timestamp: Date.now(),
      lastUpdated: new Date().toISOString(),
    };

    await db.put(STORE_NAME, dataToStore);
    return true;
  } catch (error) {
    console.error("Error storing car data to IndexedDB:", error);
    return false;
  }
};

// Utility function to convert base64/dataURL to blob URL
const convertToBlobUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null;

  if (
    dataUrl.startsWith("blob:") ||
    dataUrl.startsWith("http://") ||
    dataUrl.startsWith("https://") ||
    dataUrl.startsWith("/")
  ) {
    return dataUrl; // Existing URL, return as-is
  }

  if (dataUrl.startsWith("data:")) {
    try {
      const parts = dataUrl.split(",");
      if (parts.length !== 2) throw new Error("Invalid data URL format");
      const header = parts[0];
      const data = parts[1];
      let mimeType = "application/octet-stream";
      const mimeMatch = header.match(/data:([^;]+)/);
      if (mimeMatch) mimeType = mimeMatch[1];
      const binaryString = atob(data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error converting data URL to blob:", error);
      return null;
    }
  }

  return dataUrl; // Unknown format fallback
};

export const fetchCarData = async (sessionId) => {
  try {
    const db = await initializeDB();
    const result = await db.get(STORE_NAME, sessionId);

    if (result && result.carData) {
      const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

      if (result.timestamp > twentyFourHoursAgo) {
        // Regenerate blob URLs for all car items conditionally
        const updatedCarData = result.carData.map((car) => {
          return {
            ...car,
            isUploadBlob: car.isUploadImg
              ? convertToBlobUrl(car.isUploadImg)
              : null,
            isUploadImg: car.isUploadImg || null, // keep as-is or force null
          };
        });
        return updatedCarData;
      } else {
        await deleteCarData(sessionId);
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching car data from IndexedDB:", error);
    return null;
  }
};

// Delete car data from IndexedDB
export const deleteCarData = async (sessionId) => {
  try {
    const db = await initializeDB();
    await db.delete(STORE_NAME, sessionId);
    return true;
  } catch (error) {
    console.error("Error deleting car data from IndexedDB:", error);
    return false;
  }
};

// Get all stored sessions (optional utility function)
export const getAllSessions = async () => {
  try {
    const db = await initializeDB();
    const allData = await db.getAll(STORE_NAME);
    return allData.map((item) => ({
      sessionId: item.sessionId,
      timestamp: item.timestamp,
      lastUpdated: item.lastUpdated,
      dataSize: JSON.stringify(item.carData).length,
    }));
  } catch (error) {
    console.error("Error fetching all sessions:", error);
    return [];
  }
};

export const checkSessionExistsInIDB = async (sessionId) => {
  if (!sessionId) return false;
  try {
    const db = await openDB(DB_NAME, DB_VERSION);
    const result = await db.get(STORE_NAME, sessionId);

    // Check that result exists and carData is an array and not empty
    if (result && Array.isArray(result.carData) && result.carData.length > 0) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking IndexedDB for session:", error);
    return false;
  }
};

// Clear all stored data (optional utility function)
export const clearAllData = async () => {
  try {
    const db = await initializeDB();
    await db.clear(STORE_NAME);
    return true;
  } catch (error) {
    console.error("Error clearing all data:", error);
    return false;
  }
};

export const truncateData = async (sessionId) => {
  if (!sessionId) return;

  try {
    const db = await initializeDB();
    const existingRecord = await db.get(STORE_NAME, sessionId);

    if (existingRecord) {
      const truncatedRecord = {
        ...existingRecord,
        carData: [],
      };

      // Put the truncated record back WITHOUT passing the key separately
      await db.put(STORE_NAME, truncatedRecord);
    }
  } catch (error) {
    console.error("Error truncating data in IndexedDB:", error);
  }
};
